import { useEffect, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { FuzzyPatientClientSearchResult } from '@companion-professional/dctypes';
import { fuzzyPatientClientSearch } from '../lib/api';
import { useClinicId } from '../state/auth';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 250;

// useFuzzyPatientClientSearch debounces the search string and only hits the backend once the trimmed search string is
// at least MIN_SEARCH_LENGTH characters long. While the user is typing, the previously-fetched results are kept (so
// the dropdown doesn't flicker between requests).
export const useFuzzyPatientClientSearch = (search: string) => {
  const clinicId = useClinicId();
  const trimmed = search.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [trimmed]);

  const enabled = !!clinicId && debounced.length >= MIN_SEARCH_LENGTH;

  const { isFetching, error, data } = useQuery<FuzzyPatientClientSearchResult[]>({
    queryKey: ['fuzzy-patient-client-search', clinicId, debounced],
    queryFn: () => fuzzyPatientClientSearch(debounced),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 // 1 minute
  });

  return {
    // isPending is true while we're either waiting for the user to type enough characters or for the query to return.
    // We only consider the query "in flight" when it's actually enabled.
    isPending: enabled && isFetching,
    error: enabled ? error : null,
    results: enabled ? (data ?? []) : [],
    hasEnoughInput: enabled,
    minSearchLength: MIN_SEARCH_LENGTH
  };
};
